export type ApplicationConfig = {
  id: string
  name: string
  subdomain: string
  maxStartMileage: number
  logoUrl?: string // Base64 encoded string? URL?
  logoBase64?: string
  font?: string
  dark?: boolean
  primaryColor: string
  secondaryColor?: string
  backgroundColor?: string
  optionsLabel?: string
  options: string[]
}

export type VehicleSearchResult = {
  brand: string
  brandId: number
  model: string
  modelId: number
  modelMatch: boolean
  variant: string
  engine: string
  engineId: number
  engineMatch: boolean
  registrationYear: number
  registrationMonth: number
  servicePlanPossible: boolean
  serviceVinNumber: string
  registrationNumber: string
}

export type AgreementType = {
  id: string
  createdOn: string
  modifiedOn: string
  applicationId: string
  applicationName?: string
  agreementId: number // Used to match with Holco agreements
  name: string
  caption?: string
  descriptionLines: string[]
  maxDuration?: number
  maxStartMileage?: number
  maxTotalMileage?: number // Max. mileage for the duration of the agreement
  maxStartAge?: number
  fixedPrice: boolean
  fixedDuration?: number // When set, the duration slider is locked to this value (overrides max rules)
  fixedYearlyMileage?: number // When set, the yearly mileage slider is locked to this value (overrides max rules)
  price?: number
  includeAddons: string[]
  incompatibleAddons: string[]
  includeBrands: string[]
  excludeBrands: string[]
  customBrandId?: number // Used if agreement is fixed price
  customModelId?: number // Used if agreement is fixed price
  customEngineId?: number // Used if agreement is fixed price
}

export type AgreementTypeQuery = {
  applicationId: string
  brandId: number
  modelId: number
  engineId: number
  mileage: number
  registrationYear: number
}

export interface Agreement extends ServicePlan {
  nextServicePaymentMethod: 'include' | 'cash'
  serviceDate: string
}

export type SamAgreement = {
  id: number
  name: string
  defaultSetting: string
  description: string
}

export interface ServicePlan {
  brandId: number
  modelId: number
  engineId: number
  registrationYear: number
  oldMileage: number
  duration: number
  yearlyMileage: number
  discount: number
  discountType: string
  agreementId: number
  vinNumber: string
  licensePlate: string
  customerId: number
  startMonth: number
  startYear: number
  addons: [
    string
  ]
  agreementresponsible?: string 
}

export type CalculateDto = {
  brandId: number
  modelId: number
  engineId: number
  duration: number
  yearlyMileage: number
  oldMileage?: number
  registrationYear?: number
}

export type CalculateResponse = {
  servicePlanPrice: number
  packages: Addon[]
  additional: Addon[]
}

export type Addon = {
  id: number
  name: string
  price: number
}

export interface CustomerDto {
  name: string
  email: string
  address1: string
  zipCode: string
  city: string
  mobile: string
  status: string
}

export type CustomerResponse = {
  id: number
  name: string
  email: string
  address1: string
  address2: string
  city: string
  state: string
  zipcode: string
  phone: string
  mobile: string
  status: number
  created: string
}

export interface RequestQuoteDto extends CustomerDto {
  comments: string
  brand: string
  model: string
  engine: string
  registrationYear?: number
  licensePlate?: string
  vinNumber?: string
  oldMileage?: number
  duration: number
  yearlyMileage: number
  latestService: string
  servicePlanPrice?: number
  servicePlanPriceWithAddons?: number
  addonNames: string[]
}

export class CreateServicePlanDto {
  constructor(agreement: Partial<Agreement>, addons: Addon[] = [], agreementresponsible?: string) {
    this.brandId = agreement.brandId as number
    this.modelId = agreement.modelId as number
    this.engineId = agreement.engineId as number
    this.duration = agreement.duration as number
    this.yearlyMileage = agreement.yearlyMileage as number
    this.discount = agreement.discount as number
    this.discountType = agreement.discountType as string
    this.agreementId = agreement.agreementId as number
    this.vinNumber = agreement.vinNumber as string
    this.licensePlate = agreement.licensePlate as string
    this.customerId = agreement.customerId as number
    this.startMonth = agreement.startMonth as number + 1 // Holco months are 1-indexed
    this.startYear = agreement.startYear as number
    this.addons = [ addons.map(x => x.id).join(",") ]
    this.agreementresponsible = agreementresponsible

    if (agreement.registrationYear !== new Date().getFullYear()) {
      this.registrationYear = agreement.registrationYear as number
      this.oldMileage = agreement.oldMileage as number
    }
  }

  brandId: number
  modelId: number
  engineId: number
  registrationYear?: number
  oldMileage?: number
  duration: number
  yearlyMileage: number
  discount: number
  discountType: string
  agreementId: number
  vinNumber: string
  licensePlate: string
  customerId: number
  startMonth: number
  startYear: number
  addons: [ string ]
  agreementresponsible?: string
}

export type Brand = {
  id: number
  name: string
}

export type Model = Brand

export type Engine = Brand

export type ManualVehicle = {
  brand: Brand
  model: Model
  engine: Engine
}